import React, { FC, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import { VIDEOS_PATH } from '../../common/model/ui/video-routes';
import { getStagingConsequence } from '../../common/utils/video-staging-utils';
import { useStagingLeaveGuard } from '../../hooks/use-staging-leave-guard';
import { useVideoPermissions } from '../../hooks/use-video-permissions';
import { useVideoStagingSession } from '../../hooks/use-video-staging-session';
import breakpoints from '../../design-system/breakpoints';
import Colours from '../../design-system/colours';
import Variables from '../../design-system/variables';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvDiscardStagedFilesDialog from '../UI/molecules/OvDiscardStagedFilesDialog';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvVideoStagingSubmitError from '../UI/molecules/OvVideoStagingSubmitError';
import OvVideoStateNotice from '../UI/molecules/OvVideoStateNotice';
import OvVideoUploadValidation from '../UI/molecules/OvVideoUploadValidation';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';
import OvVideoBulkUploadForm from '../UI/organisms/OvVideoBulkUploadForm';

const VideoUpload: FC = () => {
  const { t } = useTranslation();
  const session = useVideoStagingSession();
  const { canCreate, canUpdate } = useVideoPermissions();
  const validationId = useId();
  const hasFiles = session.staged.length > 0;
  const guard = useStagingLeaveGuard(hasFiles, () => session.openDialog());
  const { placedCount, unplacedCount } = getStagingConsequence(session.staged);

  if (!(canCreate && canUpdate) && !hasFiles) {
    return (
      <Page>
        <Canvas>
          <OvListStateMessage
            icon={<LockIcon />}
            title={t('videos.bulk.noPermission')}
          >
            <BackLink to={VIDEOS_PATH}>
              {t('videos.bulk.backToVideos')}
            </BackLink>
          </OvListStateMessage>
        </Canvas>
      </Page>
    );
  }

  const backToDialog = () => {
    session.openDialog();
    guard.leave(session.returnPath);
  };

  const discard = () => {
    const path = guard.pendingPath ?? session.returnPath;
    session.discard();
    guard.leave(path);
  };

  return (
    <Page>
      <Canvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('videos.details.breadcrumb')}
          backControl={
            <BackLink to={session.returnPath}>
              <ArrowBackIcon aria-hidden="true" />
              {t('app.subPages.menu.videos')}
            </BackLink>
          }
          current={t('videos.staging.title')}
          title={t('videos.staging.title')}
          meta={
            <>
              <span>{t('videos.bulk.helper')}</span>
              <span>{t('videos.staging.keptInTab')}</span>
            </>
          }
          actions={
            <OvCompactButton
              tone="secondary"
              disabled={session.isSubmitting}
              onClick={backToDialog}
            >
              {t('videos.bulk.backToDialog')}
            </OvCompactButton>
          }
        />
        <Body>
          <OvVideoBulkUploadForm />
        </Body>
        <Footer data-viewport-sticky>
          {hasFiles && (
            <OvVideoStateNotice
              notice={{ kind: 'stagedBatch', placedCount, unplacedCount }}
              variant="compact"
            />
          )}
          <OvVideoUploadValidation
            id={validationId}
            blocker={session.blocker}
            readyCount={session.staged.length}
          />
          <OvVideoStagingSubmitError
            error={session.submitError}
            detail={session.submitErrorDetail}
          />
          <Actions>
            <OvCompactButton
              tone="secondary"
              disabled={session.isSubmitting}
              onClick={() => guard.requestLeave(session.returnPath)}
            >
              {t('common.actions.cancel')}
            </OvCompactButton>
            <OvCompactButton
              tone="primary"
              isBusy={session.isSubmitting}
              busyLabel={t('videos.staging.submitting')}
              disabled={session.blocker.kind !== 'none'}
              aria-describedby={validationId}
              onClick={() =>
                session.submit(() => guard.leave(session.returnPath))
              }
            >
              {t('videos.staging.submit', { count: session.staged.length })}
            </OvCompactButton>
          </Actions>
        </Footer>
      </Canvas>

      <OvDiscardStagedFilesDialog
        isOpen={guard.pendingPath !== null}
        onKeep={guard.stay}
        onDiscard={discard}
      />
    </Page>
  );
};

export default VideoUpload;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Variables.spacing.SMALL};
  margin-left: 1rem;
  min-width: 0;
  color: ${Colours.OV_BASE};
`;

const Canvas = styled.div`
  background-color: ${Colours.WHITE};
  border-radius: ${Variables.borderRadius.LARGE};
  box-shadow: ${Variables.boxShadow.alternateBox};

  a {
    color: ${Colours.OV_BASE};
    text-underline-offset: 3px;
  }

  a:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: bold;
  text-decoration: none;

  .MuiSvgIcon-root {
    width: 1rem;
    height: 1rem;
  }
`;

const Body = styled.div`
  padding: ${Variables.spacing.LARGE} ${Variables.spacing.XLARGE};
`;

const Footer = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: ${Variables.spacing.SMALL} ${Variables.spacing.XLARGE};
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-radius: 0 0 ${Variables.borderRadius.LARGE}
    ${Variables.borderRadius.LARGE};
  background-color: ${Colours.WHITE};

  @media (max-width: ${breakpoints.lg}) {
    bottom: ${Variables.layout.MOBILE_NAV_HEIGHT};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
`;
